import React, { useEffect, useState } from 'react';

import { MdWeb } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { searchDriverQualifications, getSearchFilterOptions } from './operations';
import useAbortableQuery from '../../../hooks/useAbortableQuery';
import {
  FormContainer,
  FormDateField,
  FormSelectField,
  FormRow,
  Breadcrumbs,
  DriverQualificationListTable,
  SubHeader,
  Button,
  ExportCsvButton,
  ModalConfirmation,
  Footer,
} from '../../../components';
import Routes from '../../../utils/routes';
import { formatIsoToDate } from '../../../utils';

import './styles.scoped.css';

const statusOptions = [
  { value: 'Requested', label: 'Requested' },
  { value: 'Assembling', label: 'Assembling' },
  { value: 'Sent', label: 'Pending Review' },
  { value: 'Client Review', label: 'Client Review' },
  { value: 'Information Requested', label: 'Information Requested' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const initialSearchState = {
  dqfNumber: '',
  driverName: '',
  status: [],
  clientName: '',
  dqSpecialist: '',
  branch: [],
  createdDateFrom: '',
  createdDateTo: '',
};

const SearchDQF = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(initialSearchState);

  const [driverOptions, setDriverOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [showValidationModal, setShowValidationModal] = useState(false);

  const { type } = useSelector(state => state.admin.user);
  const { runQuery, cancel } = useAbortableQuery();

  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoadingOptions(true);
      try {
        const { drivers, clients, branches } = await getSearchFilterOptions();

        setDriverOptions(
          drivers.map(({ driverName, driverId }) => ({
            label: `${driverName} - ${driverId}`,
            value: driverId,
            driverName,
          })),
        );

        setClientOptions(
          clients.map(({ clientName, customerId }) => ({
            label: `${clientName} - ${customerId}`,
            value: customerId,
            clientName,
          })),
        );

        setBranchOptions(
          branches.map(({ branch }) => ({
            label: String(branch),
            value: branch,
          })),
        );
      } catch (err) {
        setError('Failed to load search options. Please refresh the page.');
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchDropdownData();
  }, []);

  const handleSearch = async () => {
    const hasAnyCriteria = Object.values(searchCriteria).some(v => (Array.isArray(v) ? v.length > 0 : v !== ''));
    if (!hasAnyCriteria) {
      setShowValidationModal(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { aborted, data: response } = await runQuery(() => searchDriverQualifications(searchCriteria));
      if (aborted) return;
      setData(response);
      setShowResults(true);
    } catch (err) {
      setError('Failed to search driver qualifications. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    cancel();
    setSearchCriteria(initialSearchState);
    setData([]);
    setShowResults(false);
    setError(null);
  };

  const handleExportToExcel = () => {
    const headers = [
      'Number',
      'Branch',
      'Driver',
      'City',
      'State',
      'Status',
      'DQF Specialist',
      'Client Approver',
      'Client Name',
      'Created',
      'Updated',
    ];
    const csvRows = [headers.join(',')];

    data.forEach(item => {
      const row = [
        item.id || '',
        item.branch ?? '',
        item.driverName || '',
        typeof item.locationCity === 'string' ? item.locationCity : item.locationCity?.name || '',
        typeof item.locationState === 'string' ? item.locationState : item.locationState?.name || '',
        item.status || '',
        item.dqOwnerFullName || '',
        item.dqClientApproverUserFullName || '',
        item.name || '',
        formatIsoToDate(item.createdAt) || '',
        formatIsoToDate(item.updatedAt) || '',
      ]
        .map(value => {
          let sanitized = String(value).replace(/"/g, '""');
          if (/^[=+\-@\t\r]/.test(sanitized)) {
            sanitized = `'${sanitized}`;
          }
          return `"${sanitized}"`;
        })
        .join(',');
      csvRows.push(row);
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'dqf-search-results.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="page-container">
        <SubHeader
          text="Centerline Driver Qualification"
          buttons={[
            {
              icon: <MdWeb size={28} color="#FFF" />,
              onPress: () => navigate('/driver-qualification', { replace: true }),
            },
          ]}
          popoverTitle="Need help with your driver qualification?"
        />
        <div className="wrap">
          <Breadcrumbs
            leftTitle="DQF Home"
            middleTitle="Search"
            midItemAction={showResults ? () => setShowResults(false) : undefined}
            rightTitle={showResults ? 'Search Results' : undefined}
            route="/driver-qualification"
            wrapStyle={{ margin: 0 }}
          />
          {!showResults && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="primary"
                style={{
                  fontSize: '14px',
                  maxWidth: '115px',
                  padding: '16px',
                  textAlign: 'center',
                }}
                onPress={handleSearch}
                disabled={loading}
                text="Search"
              />
              <Button
                variant="neutral"
                style={{
                  fontSize: '14px',
                  maxWidth: '115px',
                  padding: '16px',
                  textAlign: 'center',
                }}
                onPress={handleReset}
                text="Reset Filter"
              />
            </div>
          )}
          {showResults && data.length > 0 && <ExportCsvButton onClick={handleExportToExcel} />}
        </div>

        {error && (
          <div className="error-alert" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading-container">
            <div className="spinner" />
          </div>
        ) : showResults ? (
          <div className="results-container">
            {!type ? (
              <div className="loading-container">
                <div className="spinner" />
              </div>
            ) : (
              <DriverQualificationListTable
                data={data}
                Route={type === 'Corporate' ? Routes.CorporateDQ : Routes.ClientDQ}
                showBranch
                showFileType
                showFilter={false}
                pageSize={25}
                defaultSortField="updatedAt"
              />
            )}
          </div>
        ) : (
          <FormContainer
            title="Search"
            noValidate
            onSubmit={e => e.preventDefault()}
            style={{
              '--form-label-width': '280px',
              '--form-accent-color': '#da0f2e',
              '--form-row-gap': '20px',
              '--form-padding': '24px 20px',
            }}
          >
            <FormRow>
              <p>Please select one or more criteria to search by:</p>
            </FormRow>
            <FormRow>
              <FormSelectField
                className="mb-2"
                style={{ fontSize: '16px', fontWeight: '500', width: '700px' }}
                label="Driver Name & Driver Number"
                options={[...driverOptions]}
                loading={loadingOptions}
                value={driverOptions.find(o => o.driverName === searchCriteria.driverName) ?? null}
                onChange={option => setSearchCriteria(prev => ({ ...prev, driverName: option?.driverName || '' }))}
                isSearchable
                isClearable
              />
            </FormRow>
            <FormRow>
              <FormSelectField
                className="mb-2"
                style={{ fontSize: '16px', fontWeight: '500', width: '700px' }}
                label="Customer Name & Customer Number"
                options={[...clientOptions]}
                loading={loadingOptions}
                value={clientOptions.find(o => o.clientName === searchCriteria.clientName) ?? null}
                onChange={option => setSearchCriteria(prev => ({ ...prev, clientName: option?.clientName || '' }))}
                isSearchable
                isClearable
              />
            </FormRow>
            <FormRow>
              <FormDateField
                className="mb-2"
                style={{ fontSize: '16px', fontWeight: '500', width: '700px' }}
                label="Created Date"
                range
                valueFrom={searchCriteria.createdDateFrom}
                valueTo={searchCriteria.createdDateTo}
                onChangeFrom={e => setSearchCriteria(prev => ({ ...prev, createdDateFrom: e.target.value }))}
                onChangeTo={e => setSearchCriteria(prev => ({ ...prev, createdDateTo: e.target.value }))}
              />
            </FormRow>
            <FormRow>
              <FormSelectField
                className="mb-2"
                style={{ fontSize: '16px', fontWeight: '500', minWidth: '485px', maxWidth: '100%' }}
                label="Status"
                options={statusOptions}
                value={statusOptions.filter(o => searchCriteria.status.includes(o.value))}
                onChange={selected =>
                  setSearchCriteria(prev => ({ ...prev, status: selected ? selected.map(s => s.value) : [] }))
                }
                isMulti
                isClearable
              />
            </FormRow>
            <FormRow>
              <FormSelectField
                className="mb-2"
                style={{ fontSize: '16px', fontWeight: '500', minWidth: '485px', maxWidth: '100%' }}
                label="Branch"
                options={branchOptions}
                loading={loadingOptions}
                value={branchOptions.filter(o => searchCriteria.branch.includes(o.value))}
                onChange={selected =>
                  setSearchCriteria(prev => ({ ...prev, branch: selected ? selected.map(s => s.value) : [] }))
                }
                isMulti
                isSearchable
                isClearable
              />
            </FormRow>
          </FormContainer>
        )}
        <ModalConfirmation
          isOpen={showValidationModal}
          title=""
          body="Please select at least one item to search by."
          primaryButton
          primaryButtonTitle="Ok"
          onClick={() => setShowValidationModal(false)}
        />
      </div>
      <Footer />
    </>
  );
};

export default SearchDQF;
